import { useState } from 'react';
import { Box, Text, Flex, Button, Textarea } from '@chakra-ui/react';
import { Clock, User, Check, XCircle } from 'lucide-react';
import { ExpandableClampText } from './ExpandableClampText';
import { DurationStepper } from '@/components/ui/DurationStepper';
import { validateDuration } from '@/utils/volunteerHours';

const REVIEW_BADGE = {
  label: 'Review',
  fg: '#B45309',
  bg: '#FEF3C7',
  accent: '#F59E0B',
};

function formatSessionWindow(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  return `${start.toLocaleDateString()} · ${start.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })} – ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

export interface TaskReviewCardProps {
  taskTitle: string;
  volunteerName: string;
  description: string | null;
  startTime: string;
  endTime: string;
  /** Time to accept, in minutes. */
  minutes: number;
  onMinutesChange: (minutes: number) => void;
  modified: boolean;
  onAccept: () => void;
  onDeny: (reason: string) => void;
  /** An accept/deny for this item is being saved — a second click would repeat it. */
  busy?: boolean;
}

export function TaskReviewCard({
  taskTitle,
  volunteerName,
  description,
  startTime,
  endTime,
  minutes,
  onMinutesChange,
  modified,
  onAccept,
  onDeny,
  busy = false,
}: TaskReviewCardProps) {
  const [denyPending, setDenyPending] = useState(false);
  const [denyReason, setDenyReason] = useState('');

  const sessionLabel = formatSessionWindow(startTime, endTime);
  const minutesValid = validateDuration(minutes) === null;

  return (
    <Box
      bg="white"
      borderRadius="xl"
      borderWidth="1px"
      borderColor="volyx.borderLight"
      borderLeftWidth="4px"
      borderLeftColor={REVIEW_BADGE.accent}
      boxShadow="sm"
      transition="border-color 0.15s, box-shadow 0.15s"
      _hover={{ boxShadow: 'md' }}
    >
      <Box p="4" pl="3.5">
        <Flex align="flex-start" justify="space-between" gap="2" mb="1.5">
          <Box flex="1" minW="0">
            <ExpandableClampText
              text={taskTitle}
              lines={2}
              fontSize="sm"
              fontWeight="semibold"
              color="volyx.primary"
              mb="0"
            />
          </Box>
          <Box px="2.5" py="0.5" borderRadius="full" bg={REVIEW_BADGE.bg} flexShrink={0}>
            <Text
              fontSize="10px"
              fontWeight="bold"
              color={REVIEW_BADGE.fg}
              m="0"
              letterSpacing="0.04em"
              textTransform="uppercase"
            >
              {REVIEW_BADGE.label}
            </Text>
          </Box>
        </Flex>

        {description ? (
          <ExpandableClampText text={description} lines={2} mb="3" />
        ) : null}

        <Flex direction="column" gap="1.5" mb="3">
          <Flex align="center" gap="2" wrap="wrap">
            <Clock size={14} color="#64748B" />
            <Text fontSize="xs" color="volyx.textSecondary" m="0">
              {sessionLabel}
            </Text>
          </Flex>
          <Flex align="center" gap="2">
            <User size={14} color="#64748B" />
            <Text fontSize="xs" color="volyx.textSecondary" m="0" lineClamp={1}>
              {volunteerName}
            </Text>
          </Flex>
        </Flex>

        <Flex alignItems="center" gap="3" mb="3" flexWrap="wrap">
          <Text fontSize="xs" color="#000" flexShrink={0} m="0" fontWeight="bold">
            Time to accept
          </Text>
          <DurationStepper size="sm" value={minutes} onChange={onMinutesChange} />
          {modified ? (
            <Text fontSize="10px" color="orange.500" m="0" flexShrink={0} fontWeight="semibold">
              modified
            </Text>
          ) : null}
        </Flex>

        {denyPending ? (
          <Box>
            <Text fontSize="xs" fontWeight="semibold" color="volyx.textSecondary" m="0" mb="1.5">
              Reason for denial (optional)
            </Text>
            <Textarea
              value={denyReason}
              onChange={(e) => setDenyReason(e.target.value)}
              placeholder="Explain what the volunteer should fix or resubmit…"
              minH="72px"
              borderRadius="xl"
              borderColor="volyx.borderLight"
              bg="volyx.lightGray"
              resize="vertical"
              fontSize="sm"
              mb="3"
              _focus={{
                borderColor: 'volyx.action',
                bg: 'white',
                boxShadow: '0 0 0 1px var(--chakra-colors-volyx-action)',
              }}
            />
            <Flex gap="2" align="stretch">
              <Button
                flex="1"
                minH="40px"
                size="sm"
                borderRadius="lg"
                bg="transparent"
                color="volyx.textSecondary"
                border="1px solid"
                borderColor="volyx.mediumGray"
                fontWeight="semibold"
                _hover={{ bg: 'volyx.lightGray' }}
                onClick={() => {
                  setDenyPending(false);
                  setDenyReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                flex="1"
                minH="40px"
                size="sm"
                borderRadius="lg"
                bg="transparent"
                color="red.600"
                border="1px solid"
                borderColor="red.200"
                fontWeight="semibold"
                _hover={{ bg: 'red.50', borderColor: 'red.400' }}
                onClick={() => {
                  onDeny(denyReason.trim());
                  setDenyPending(false);
                  setDenyReason('');
                }}
              >
                <Flex as="span" align="center" gap="1.5" justify="center" w="full">
                  <XCircle size={14} />
                  Confirm deny
                </Flex>
              </Button>
            </Flex>
          </Box>
        ) : (
          <Flex gap="2" align="stretch">
            <Button
              flex="1"
              minH="40px"
              size="sm"
              borderRadius="lg"
              bg="volyx.success"
              color="white"
              fontWeight="semibold"
              disabled={!minutesValid || busy}
              _hover={{ opacity: 0.92 }}
              _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
              onClick={onAccept}
            >
              <Flex as="span" align="center" gap="1.5" justify="center" w="full">
                <Check size={14} />
                Accept
              </Flex>
            </Button>
            <Button
              flex="1"
              minH="40px"
              size="sm"
              borderRadius="lg"
              bg="transparent"
              color="red.600"
              border="1px solid"
              borderColor="red.200"
              fontWeight="semibold"
              disabled={busy}
              _hover={{ bg: 'red.50', borderColor: 'red.400' }}
              _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
              onClick={() => setDenyPending(true)}
            >
              <Flex as="span" align="center" gap="1.5" justify="center" w="full">
                <XCircle size={14} />
                Deny
              </Flex>
            </Button>
          </Flex>
        )}
      </Box>
    </Box>
  );
}
